import React from 'react';

interface AdminFieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

export function AdminField({ label, children, className = '' }: AdminFieldProps) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="text-brand-text-muted text-xs font-medium uppercase tracking-wider">
        {label}
      </label>
      {children}
    </div>
  );
}

interface AdminInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export function AdminInput({ label, ...props }: AdminInputProps) {
  return (
    <AdminField label={label}>
      <input
        {...props}
        className={`w-full bg-brand-dark/60 border border-brand-gold/20 rounded-md py-2.5 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/50 focus:outline-none focus:border-brand-gold/50 transition-colors ${props.className || ''}`}
      />
    </AdminField>
  );
}

interface AdminTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
}

export function AdminTextarea({ label, ...props }: AdminTextareaProps) {
  return (
    <AdminField label={label}>
      <textarea
        {...props}
        className={`w-full bg-brand-dark/60 border border-brand-gold/20 rounded-md py-2.5 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/50 focus:outline-none focus:border-brand-gold/50 transition-colors resize-none ${props.className || ''}`}
      />
    </AdminField>
  );
}

interface AdminSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: { value: string; label: string }[];
}

export function AdminSelect({ label, options, ...props }: AdminSelectProps) {
  return (
    <AdminField label={label}>
      <select
        {...props}
        className={`w-full bg-brand-dark/60 border border-brand-gold/20 rounded-md py-2.5 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50 transition-colors appearance-none ${props.className || ''}`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </AdminField>
  );
}

interface AdminToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function AdminToggle({ label, checked, onChange }: AdminToggleProps) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-brand-gold' : 'bg-brand-dark/80 border border-brand-gold/30'}`}
        onClick={() => onChange(!checked)}
      >
        <div
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
        />
      </div>
      <span className="text-brand-text text-sm group-hover:text-white transition-colors">
        {label}
      </span>
    </label>
  );
}

interface AdminSectionCardProps {
  title: string;
  icon: string;
  children: React.ReactNode;
}

export function AdminSectionCard({ title, icon, children }: AdminSectionCardProps) {
  return (
    <div className="bg-brand-card/60 border border-brand-gold/10 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-brand-gold/10">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className={`${icon} text-brand-gold text-lg`} />
        </div>
        <h2 className="text-white font-semibold text-sm tracking-wide uppercase">{title}</h2>
      </div>
      <div className="p-5 space-y-5">
        {children}
      </div>
    </div>
  );
}